import { type ReactNode } from 'react';
import type { Tier } from '../hooks/use-pricing-table';

const BASE = 'w-16 rounded-md border bg-surface px-2 py-1 text-xs';
const STYLE = 'text-text-primary tabular-nums transition-colors duration-200';
const FOCUS =
  'focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red/30';
const INPUT = `${BASE} ${STYLE} ${FOCUS}`;
const OK = 'border-border-subtle';
const ERR = 'border-error/50';

function cls(hasError: boolean, extra?: string): string {
  return `${INPUT} ${hasError ? ERR : OK} ${extra ?? ''}`;
}

type Props = {
  tier: Tier;
  onChange: (t: Tier) => void;
  isFirst: boolean;
  isLast: boolean;
  hasError: boolean;
};

function MinInput({ tier, onChange, isFirst, hasError }: Props): ReactNode {
  return (
    <input
      type="number"
      value={tier.min}
      onChange={(e) => onChange({ ...tier, min: +e.target.value })}
      disabled={isFirst}
      className={cls(hasError, isFirst ? 'opacity-50 cursor-not-allowed' : '')}
    />
  );
}

function MaxInput({ tier, onChange, isLast, hasError }: Props): ReactNode {
  if (isLast)
    return (
      <span className="w-16 text-center text-xs text-text-tertiary">∞</span>
    );
  return (
    <input
      type="number"
      value={tier.max ?? ''}
      onChange={(e) => onChange({ ...tier, max: +e.target.value })}
      className={cls(hasError)}
    />
  );
}

export function TierRange(p: Props): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      <MinInput {...p} />
      <span className="text-[10px] text-text-tertiary">—</span>
      <MaxInput {...p} />
    </div>
  );
}

export function TierPrice({
  tier,
  onChange,
  hasError,
}: {
  tier: Tier;
  onChange: (t: Tier) => void;
  hasError: boolean;
}): ReactNode {
  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        step="0.01"
        value={tier.price_per_lead}
        onChange={(e) => onChange({ ...tier, price_per_lead: +e.target.value })}
        className={cls(hasError, 'w-20')}
      />
      <span className="text-[10px] text-text-tertiary">$</span>
    </div>
  );
}
